// PartnerStep - Partner selection for worksheet wizard
import type { Partner as ApiPartner } from '@/api/partners';
import { ExpiredIdCardOverride, PartnerSearchSelect } from '@/components/partners';
import { useTranslation } from '@kgc/i18n';
import { useCallback } from 'react';
import type { Partner } from '../../rental/types';
import { useWorksheetWizardStore } from '../wizard-store';

/** Map API Partner to local wizard Partner type */
function toWizardPartner(p: ApiPartner): Partner {
  return {
    id: p.id,
    name: p.name,
    email: p.email,
    phone: p.phone,
    type: p.type === 'company' ? 'COMPANY' : 'INDIVIDUAL',
    status: (p.status as Partner['status']) ?? 'active',
    taxNumber: p.taxNumber,
    address: p.address,
    city: p.city,
    isVip: p.loyaltyPoints > 1000,
    creditLimit: p.creditLimit ?? 0,
    balance: p.currentBalance,
    warningNote: p.warningNote,
    costBearerId: p.costBearerId,
    costBearerName: p.costBearerName,
    maskedIdCardNumber: p.maskedIdCardNumber,
    // Story 70-1E-a: forward expiry for override UI
    idCardExpiry: p.idCardExpiry,
  };
}

export function PartnerStep() {
  const { t } = useTranslation();
  const { partner: selectedPartner, setPartner, setExpiredIdOverride } = useWorksheetWizardStore();

  const handleSelect = (apiPartner: ApiPartner) => {
    setPartner(toWizardPartner(apiPartner));
  };

  const handleOverrideChange = useCallback(
    (state: { override: boolean; reason: string }) => {
      setExpiredIdOverride(state.override, state.reason);
    },
    [setExpiredIdOverride]
  );

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t('service.selectPartnerHeading')}
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">{t('service.selectPartnerDesc')}</p>
      </div>

      <PartnerSearchSelect
        onSelect={handleSelect}
        placeholder={t('service.searchPartnerPlaceholder')}
        value={selectedPartner ? ({ id: selectedPartner.id } as ApiPartner) : null}
        renderExtra={partner => (
          <>
            {partner.costBearerName && (
              <div className="mt-2 text-xs text-muted-foreground">
                {t('service.costBearer', { name: partner.costBearerName })}
              </div>
            )}
          </>
        )}
      />

      {selectedPartner && (
        <div className="rounded-lg bg-green-50 dark:bg-green-900/20 p-4">
          <p className="text-sm font-medium text-green-800 dark:text-green-300">
            {t('service.selectedPartner', { name: selectedPartner.name })}
          </p>
        </div>
      )}

      {/* Story 70-1E-a: expired ID card override (renders only when expired) */}
      {selectedPartner && (
        <ExpiredIdCardOverride
          idCardExpiry={selectedPartner.idCardExpiry}
          onChange={handleOverrideChange}
          data-testid="worksheet-partner-expired-override"
        />
      )}
    </div>
  );
}
